import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { H2, H4 } from '@/components/ui/typography';
import { PersonaImportContext } from '@/contexts/PersonaImportContext';
import { ChevronLeft } from '@/icons/ChevronLeft';
import { ChevronRight } from '@/icons/ChevronRight';
import { ExternalLink } from '@/icons/ExternalLink';
import { cn } from '@/utils/cn';
import Head from 'expo-router/head';
import { VideoView, useVideoPlayer } from 'expo-video';
import * as WebBrowser from 'expo-web-browser';
import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import {
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const IMAGE_SOURCE = require('../../assets/images/persona2.png');
const VIDEO_SOURCE = require('../../assets/videos/scrolling.mp4');

interface Instruction {
  text: string;
  label: string;
  url?: string;
}

type PlatformOS = 'macos' | 'windows' | 'ios' | 'android' | 'linux' | 'other';

const INSTRUCTIONS: Record<PlatformOS, Instruction> = {
  macos: {
    label: 'Mac OS',
    text: 'Press Command + Shift + 5 to open the screen recording tool.',
    url: 'https://support.apple.com/en-us/HT208721'
  },
  windows: {
    label: 'Windows',
    text: 'Press Windows + Shift + S to open the screen snipping tool.',
    url: 'https://support.microsoft.com/en-us/windows/use-snipping-tool-to-capture-screenshots-00246869-1843-655f-f220-97299b865f6b'
  },
  ios: {
    label: 'iOS',
    text: 'Swipe down from the top-right corner of your screen and tap the gray Record button. Exit Control Center to record your screen.',
    url: 'https://support.apple.com/en-us/HT207935'
  },
  android: {
    label: 'Android',
    text: 'Swipe down from the top of your screen to open the notification panel, then tap the screen recording icon.',
    url: 'https://support.google.com/android/answer/9075928?hl=en'
  },
  linux: {
    label: 'Linux',
    text: 'SimpleScreenRecorder can be used to record your screen.',
    url: 'https://www.maartenbaert.be/simplescreenrecorder/'
  },
  other: {
    label: 'Other',
    text: "Sorry, we're not able to provide instructions for your current platform."
  }
};

export default function PersonaHow() {
  const os: PlatformOS = Platform.OS === 'web' ? getBaseOsWeb() : Platform.OS;

  const availableInstructions =
    os === 'other'
      ? INSTRUCTIONS
      : Object.keys(INSTRUCTIONS).reduce(
          (acc, key) => {
            if (key === 'other') return acc;
            acc[key as PlatformOS] = INSTRUCTIONS[key as PlatformOS];
            return acc;
          },
          {} as Record<PlatformOS, Instruction>
        );

  const availableInstructionsArr = Object.keys(availableInstructions);
  const { onNext } = useContext(PersonaImportContext);

  const player = useVideoPlayer(VIDEO_SOURCE, (player) => {
    player.loop = true;
    player.muted = true;
  });

  const [showingPlayer, setShowingPlayer] = useState(false);

  const [viewingInstructions, setViewingInstructions] = useState<
    PlatformOS | true | null
  >(null);

  const [viewedInstructions, setViewedInstructions] = useState(false);

  const instruction =
    viewingInstructions && viewingInstructions !== true
      ? INSTRUCTIONS[viewingInstructions]
      : INSTRUCTIONS[os];

  const openWebBrowser = useCallback((url: string) => {
    WebBrowser.openBrowserAsync(url);
  }, []);

  useEffect(() => {
    if (showingPlayer) {
      player.play();
    }
  }, [showingPlayer, player]);

  useEffect(() => {
    if (
      (viewingInstructions && viewingInstructions !== true) ||
      showingPlayer
    ) {
      setViewedInstructions(true);
    }
  }, [viewingInstructions, showingPlayer]);

  return (
    <Fragment>
      <Head>
        <title>New Persona - Lexikon AI</title>
      </Head>
      <SafeAreaView edges={['right', 'bottom', 'left']} className="flex-1">
        <ScrollView contentContainerClassName="flex">
          <View className="px-12 pt-4 web:lg:w-[50%] web:self-center">
            <View className="items-center pb-1">
              <Image
                source={IMAGE_SOURCE}
                style={styles.image}
                resizeMode="contain"
              />
              <H2 className="border-0 pb-1 pt-4 text-center">
                How to record your screen
              </H2>

              <Card className="mt-4 p-4 w-full">
                {showingPlayer ? (
                  <Fragment>
                    <Pressable
                      className="items-start"
                      style={styles.backButton}
                      onPress={() => setShowingPlayer(false)}
                    >
                      <View className="mb-2 flex-row items-center gap-1">
                        <ChevronLeft className="text-primary" />
                        <Text className="text-primary">Back</Text>
                      </View>
                    </Pressable>
                    <Text>
                      Starting from the bottom (most recent messages), scroll
                      slowly up through your messages, about one 'screen' per
                      second. Crop any non-message content from the recording.
                    </Text>

                    <VideoView
                      style={styles.video}
                      player={player}
                      allowsFullscreen
                    />
                  </Fragment>
                ) : viewingInstructions ? (
                  viewingInstructions === true ? (
                    <Fragment>
                      <H4 className="border-0 pb-2">View instructions for…</H4>

                      {Object.entries(availableInstructions).map(
                        ([key, instruction]) => (
                          <Pressable
                            key={key}
                            className={cn(
                              'px-2 py-3',
                              availableInstructionsArr.indexOf(key) !==
                                availableInstructionsArr.length - 1 &&
                                'border-b border-neutral-200 dark:border-neutral-700'
                            )}
                            onPress={() => {
                              setViewingInstructions(key as PlatformOS);
                            }}
                          >
                            <View className="w-full flex-row items-center justify-between gap-3">
                              <Text className="text-primary">
                                {instruction.label}
                              </Text>
                              <ChevronRight className="text-primary" />
                            </View>
                          </Pressable>
                        )
                      )}

                      {viewedInstructions ? (
                        <View className="mt-4">
                          <Button onPress={() => onNext()}>
                            <Text className="font-medium">Continue</Text>
                          </Button>
                        </View>
                      ) : (
                        <Text className="text-center">
                          We recommend recording from your mobile device for
                          best results.
                        </Text>
                      )}
                    </Fragment>
                  ) : (
                    <Fragment>
                      <Pressable
                        className="items-start"
                        style={styles.backButton}
                        onPress={() => setViewingInstructions(true)}
                      >
                        <View className="mb-2 flex-row items-center gap-1">
                          <ChevronLeft className="text-primary" />
                          <Text className="text-primary">Back</Text>
                        </View>
                      </Pressable>
                      <H2 className="border-0 pb-4">{instruction.label}</H2>
                      <Text>{instruction.text}</Text>

                      <Text className="pt-4">
                        While recording, scroll slowly through the messages you
                        want to use to create your AI. Do not include any other
                        content in the recording.
                      </Text>

                      <Button
                        className="mt-4 flex"
                        variant="outline"
                        onPress={() => setShowingPlayer(true)}
                      >
                        <View className="flex flex-row items-center gap-1 align-middle">
                          <Text className="font-medium text-primary">
                            See Example
                          </Text>
                        </View>
                      </Button>

                      {instruction.url && (
                        <Button
                          variant="secondary"
                          className="mt-4"
                          onPress={() => openWebBrowser(instruction.url!)}
                        >
                          <View className="flex-row items-center gap-1">
                            <Text className="text-primary">Learn More</Text>
                            <ExternalLink className="text-primary" size={15} />
                          </View>
                        </Button>
                      )}
                    </Fragment>
                  )
                ) : (
                  <Fragment>
                    <Text>
                      {instruction.label ? (
                        <Text className="font-semibold">
                          {instruction.label}:{' '}
                        </Text>
                      ) : null}
                      {instruction.text}
                    </Text>

                    {['linux', 'macos', 'windows', 'other'].includes(os) && (
                      <Text className="pt-4">
                        <Text className="font-semibold">Note: </Text>
                        We recommend recording from your mobile device for best
                        results.
                      </Text>
                    )}

                    <Text className="pt-4">
                      While recording, scroll slowly through the messages you
                      want to use to create your AI. Do not include any other
                      content in the recording.
                    </Text>

                    <Button
                      variant="outline"
                      className="mt-4 flex"
                      onPress={() => setShowingPlayer(true)}
                    >
                      <View className="flex flex-row items-center gap-1 align-middle">
                        <Text className="font-medium text-primary">
                          See Example
                        </Text>
                      </View>
                    </Button>

                    <View className="mt-4">
                      <Button
                        variant="secondary"
                        onPress={() => setViewingInstructions(true)}
                      >
                        <View className="flex flex-row items-center">
                          <Text className="font-medium text-primary">
                            More Instructions
                          </Text>
                        </View>
                      </Button>
                    </View>

                    {viewedInstructions && (
                      <View className="mt-4">
                        <Button onPress={() => onNext()}>
                          <Text className="font-medium">Continue</Text>
                        </Button>
                      </View>
                    )}
                  </Fragment>
                )}
              </Card>
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Fragment>
  );
}

const styles = StyleSheet.create({
  image: {
    width: 250,
    height: 250
  },
  video: {
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
    borderRadius: 8,
    width: Platform.OS === 'web' ? undefined : 250,
    height: Platform.OS === 'web' ? undefined : 250
  },
  backButton: {
    paddingLeft: 0
  }
});

// adepted from https://github.com/react-native-device-info/react-native-device-info/blob/fee1db459cccb98428b8e28f37f506a71d2291ff/src/web/index.js#L87-L109
function getBaseOsWeb(): PlatformOS {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];

  let os = platform;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'macos';
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'ios';
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'windows';
  } else if (/Android/.test(userAgent)) {
    os = 'android';
  } else if (!os && /Linux/.test(platform)) {
    os = 'linux';
  }

  if (INSTRUCTIONS.hasOwnProperty(os)) {
    return os as PlatformOS;
  }
  return 'other';
}
